import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TipoMarcacao } from '@arena/shared/dominio';
import type {
  EscopoDeAlteracao,
  GradeDoDia as Grade,
  MarcacaoNaGrade,
} from '@arena/shared/schemas';
import {
  adicionarDias,
  dataValida,
  diaDaGrade,
  horaDe,
  horaNoRelogio,
  minutosNaGrade,
  noCalendario,
  partesNoFuso,
} from '@arena/shared/tempo';
import { agendaApi } from '../api/agenda.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import {
  Botao,
  BotaoDeIcone,
  Card,
  Feed,
  ItemDeFeed,
  Kpi,
  Nota,
  Vazio,
} from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  corSolidaDaMarcacao,
  dataPorExtenso,
  dinheiro,
  horaCurta,
  horaNaArena,
  horas,
  intervaloCurto,
  subtituloDaMarcacao,
  tituloDaMarcacao,
} from './formatos.js';
import { GradeDoDia } from './GradeDoDia.jsx';
import {
  ListaDeHorasACobrar,
  ResumoDeHorasACobrar,
  useConsultaDeHorasACobrar,
} from './HorasACobrar.jsx';
import { ModalDaMarcacao } from './ModalDaMarcacao.jsx';
import { ModalDeReserva } from './ModalDeReserva.jsx';
// 14 · site
import { CardDePedidosDoSite } from '../site/CardDePedidos.jsx';

/**
 * `#v-agenda` — o primeiro marco demonstrável do plano.
 *
 * A grade das duas quadras, os KPIs do dia e as janelas livres. O
 * estado que importa mora na URL — `?data=`, `?nova=1`, `?marcacao=` —
 * para que o botão "Nova reserva" da topbar, o link de "Próximas na
 * agenda" do painel e o F5 caiam todos no mesmo lugar.
 *
 * Tempo real é POLLING: a grade se reconsulta a cada 15 segundos
 * enquanto a aba está visível. Para duas quadras e uma dezena de
 * pessoas é indistinguível de tempo real, e o Realtime do Supabase foi
 * descartado na arquitetura. SSE sobre LISTEN/NOTIFY entra quando isto
 * incomodar.
 *
 * Ver: Arena_backend/Docs/plano/06-agenda-quadras.md
 */

const INTERVALO_DE_ATUALIZACAO_MS = 15_000;

interface PedidoDeMover {
  readonly marcacao: MarcacaoNaGrade;
  readonly quadraId: string;
  readonly hora: string;
}

export function PaginaDaAgenda() {
  const [params, setParams] = useSearchParams();
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();

  const dataPedida = params.get('data') ?? undefined;
  const dataValidaPedida = dataPedida && dataValida(dataPedida) ? dataPedida : undefined;

  const consulta = useQuery({
    queryKey: ['agenda', 'grade', dataValidaPedida ?? 'hoje'],
    queryFn: () => agendaApi.grade(dataValidaPedida),
    refetchInterval: INTERVALO_DE_ATUALIZACAO_MS,
    // Ao trocar de dia, a grade anterior fica na tela até a nova chegar
    // — em vez de piscar um esqueleto a cada clique na seta.
    placeholderData: keepPreviousData,
  });

  const [falha, setFalha] = useState<string | null>(null);
  const [mover, setMover] = useState<PedidoDeMover | null>(null);
  // O drill-down do KPI "Previsto de locação": o que o dia tem para
  // cobrar, hora por hora, com a baixa a um clique.
  const [previsto, setPrevisto] = useState(false);

  const alterar = useMutation({
    mutationFn: (pedido: {
      id: string;
      quadraId: string;
      /** Só quando o soltar muda o dia do calendário — a madrugada é do dia seguinte. */
      data?: string;
      hora: string;
      escopo?: EscopoDeAlteracao;
    }) =>
      agendaApi.alterar(pedido.id, {
        quadraId: pedido.quadraId,
        ...(pedido.data ? { data: pedido.data } : {}),
        inicio: pedido.hora,
        ...(pedido.escopo ? { escopo: pedido.escopo } : {}),
      }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['agenda'] }),
  });

  const podeCriar = pode('agenda', 'criar');
  const podeEditar = pode('agenda', 'editar');

  function mudarParams(mudanca: (p: URLSearchParams) => void) {
    const novos = new URLSearchParams(params);
    mudanca(novos);
    setParams(novos, { replace: true });
  }

  if (consulta.isPending) {
    return (
      <>
        <CabecalhoDaAgenda titulo="Carregando a grade…" />
        <div className="grid g4">
          {[0, 1, 2, 3].map((i) => (
            <EsqueletoDeCard key={i} linhas={2} />
          ))}
        </div>
        <EsqueletoDeCard linhas={10} />
      </>
    );
  }

  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <>
        <CabecalhoDaAgenda titulo="Não consegui carregar a grade" />
        <Erro
          tipo={
            erro instanceof ErroDeRede
              ? 'rede'
              : erro instanceof ErroDeApi && erro.ehPermissao
                ? 'permissao'
                : 'falha'
          }
          codigo={erro instanceof ErroDeApi ? erro.requestId : undefined}
          onTentarDeNovo={() => void consulta.refetch()}
        />
      </>
    );
  }

  const grade = consulta.data;
  // O dia da GRADE: à 01:00 de domingo, na arena que fecha às 02:00, o
  // "hoje" ainda é sábado — o mesmo que o servidor responde sem `?data=`.
  const hoje = diaDaGrade(new Date(), grade.horarioFuncionamento, grade.fuso);
  /** A hora de um instante na grade deste dia — "25:00" para a 01:00 seguinte. */
  const horaNaGrade = (instante: string) =>
    horaDe(minutosNaGrade(instante, grade.data, grade.fuso));
  const ehHoje = grade.data === hoje;
  const nomeDaQuadra = new Map(grade.quadras.map((q) => [q.id, q.nome]));

  const irPara = (data: string) =>
    mudarParams((p) => {
      if (data === hoje) p.delete('data');
      else p.set('data', data);
    });
  const abrirNova = (quadraId?: string, hora?: string) =>
    mudarParams((p) => {
      p.set('nova', '1');
      if (quadraId) p.set('quadra', quadraId);
      else p.delete('quadra');
      if (hora) p.set('hora', hora);
      else p.delete('hora');
    });
  const fecharNova = () =>
    mudarParams((p) => {
      p.delete('nova');
      p.delete('quadra');
      p.delete('hora');
    });
  const abrirMarcacao = (id: string) => mudarParams((p) => p.set('marcacao', id));
  const fecharMarcacao = () => mudarParams((p) => p.delete('marcacao'));

  async function moverPara(pedido: PedidoDeMover, escopo?: EscopoDeAlteracao) {
    setFalha(null);
    // A hora do soltar é da grade; a API fala calendário. A data só vai
    // quando muda — o que só acontece cruzando a meia-noite.
    const destino = noCalendario(grade.data, pedido.hora);
    const dataAtual = partesNoFuso(pedido.marcacao.inicio, grade.fuso).data;
    try {
      const resultado = await alterar.mutateAsync({
        id: pedido.marcacao.id,
        quadraId: pedido.quadraId,
        ...(destino.data !== dataAtual ? { data: destino.data } : {}),
        hora: destino.hora,
        ...(escopo ? { escopo } : {}),
      });
      const para = `${nomeDaQuadra.get(pedido.quadraId) ?? 'quadra'} às ${horaCurta(horaNoRelogio(pedido.hora))}`;
      avisar(
        resultado.alteradas > 1
          ? `Série movida para ${para}: ${resultado.alteradas} datas daqui em diante.`
          : `Marcação movida para ${para}.`,
      );
    } catch (erro) {
      // Erro fica na tela até alguém fechar — nunca num toast que some.
      setFalha(mensagemDoErro(erro));
    }
  }

  function aoSoltar(marcacao: MarcacaoNaGrade, quadraId: string, hora: string) {
    if (marcacao.quadraId === quadraId && horaNaGrade(marcacao.inicio) === hora) return;
    if (marcacao.serieId) setMover({ marcacao, quadraId, hora });
    else void moverPara({ marcacao, quadraId, hora });
  }

  const nova = params.get('nova') === '1';
  const marcacaoAberta = params.get('marcacao');

  return (
    <>
      <CabecalhoDaAgenda
        titulo="Agenda das quadras"
        subtitulo={`${dataPorExtenso(grade.data)} · ${
          podeEditar
            ? 'arraste um card para remarcar, o sistema revalida o conflito.'
            : 'a grade das duas quadras, atualizada sozinha.'
        }`}
        acoes={
          <>
            <div className="nav-dia">
              <span className="anterior">
                <BotaoDeIcone
                  icone="go"
                  aria-label="Dia anterior"
                  onClick={() => irPara(adicionarDias(grade.data, -1))}
                />
              </span>
              <Botao variante="contorno" pequeno disabled={ehHoje} onClick={() => irPara(hoje)}>
                Hoje
              </Botao>
              <BotaoDeIcone
                icone="go"
                aria-label="Dia seguinte"
                onClick={() => irPara(adicionarDias(grade.data, 1))}
              />
              <input
                type="date"
                aria-label="Escolher o dia"
                value={grade.data}
                onChange={(e) => {
                  if (dataValida(e.target.value)) irPara(e.target.value);
                }}
              />
            </div>
            {podeCriar && (
              <Botao icone="plus" onClick={() => abrirNova()}>
                Nova reserva
              </Botao>
            )}
          </>
        }
      />

      <Nota>
        <b>Agenda e quadras.</b> Cada quadra tem a sua cor — Quadra 1 em vermelho e Quadra 2 em
        azul, como no levantamento — e cada card mostra o horário, quem reservou, quantas pessoas e
        a situação do pagamento. Locação avulsa, evento e bloqueio têm identidade própria; turma
        fixa e aula experimental chegam com as turmas e o site. A grade se atualiza sozinha a cada
        15 segundos enquanto esta aba está aberta.
      </Nota>

      <IndicadoresDoDia grade={grade} ehHoje={ehHoje} onAbrirPrevisto={() => setPrevisto(true)} />

      {falha && (
        <div className="aviso conflito" role="alert">
          <Icone nome="info" />
          <div>
            <b>Não deu para mover.</b> {falha}
          </div>
          <button
            type="button"
            className="fechar"
            aria-label="Fechar aviso"
            onClick={() => setFalha(null)}
          >
            <Icone nome="x" />
          </button>
        </div>
      )}

      {/* `overflow: clip` e não `hidden`: arredonda os cantos sem virar
          contêiner de rolagem, e o cabeçalho das quadras continua grudado
          no topo ao rolar a página. */}
      <article className="card" style={{ padding: 0, overflow: 'clip' }}>
        <GradeDoDia
          grade={grade}
          podeCriar={podeCriar}
          podeMover={podeEditar}
          onLivre={(quadraId, hora) => abrirNova(quadraId, hora)}
          onAbrir={(marcacao) => abrirMarcacao(marcacao.id)}
          onMover={aoSoltar}
        />
      </article>

      {/* 14 · site — "Pedidos do site", para quem responde a fila (cosmético: o servidor decide). */}
      <div className={`grid ${podeEditar ? 'g3' : 'g2'}`}>
        {podeEditar && <CardDePedidosDoSite />}
        <Card
          titulo={ehHoje ? 'Janelas livres hoje' : 'Janelas livres do dia'}
          dica={ehHoje ? 'daqui para frente' : undefined}
        >
          {grade.janelasLivres.length === 0 ? (
            <Vazio>Nenhuma janela livre{ehHoje ? ' daqui para frente' : ' neste dia'}.</Vazio>
          ) : (
            <Feed>
              {grade.janelasLivres.map((janela) => (
                <ItemDeFeed
                  key={`${janela.quadraId}-${janela.inicio}`}
                  marca={
                    <span className="sq" style={{ background: 'var(--panel-3)' }}>
                      <Icone nome="ball" />
                    </span>
                  }
                  titulo={`${nomeDaQuadra.get(janela.quadraId) ?? 'Quadra'} · ${intervaloCurto(janela.inicio, janela.fim, grade.fuso)}`}
                  subtitulo={`${horas(janela.minutos)} livres`}
                  {...(podeCriar
                    ? {
                        onClick: () => abrirNova(janela.quadraId, horaNaGrade(janela.inicio)),
                      }
                    : {})}
                />
              ))}
            </Feed>
          )}
        </Card>

        <Card
          titulo="Canceladas no dia"
          dica={grade.canceladas.length ? `${grade.canceladas.length}` : undefined}
        >
          {grade.canceladas.length === 0 ? (
            <Vazio>Nenhum cancelamento neste dia.</Vazio>
          ) : (
            <Feed>
              {grade.canceladas.map((m) => (
                <ItemDeFeed
                  key={m.id}
                  marca={
                    <span
                      className="sq"
                      style={{
                        background: corSolidaDaMarcacao(
                          m,
                          grade.quadras.find((q) => q.id === m.quadraId),
                        ),
                        opacity: 0.55,
                      }}
                    >
                      <Icone nome="x" />
                    </span>
                  }
                  titulo={`${tituloDaMarcacao(m)} · ${horaCurta(horaNaArena(m.inicio, grade.fuso))}`}
                  subtitulo={[
                    nomeDaQuadra.get(m.quadraId),
                    subtituloDaMarcacao(m),
                    m.motivoCancelamento,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  onClick={() => abrirMarcacao(m.id)}
                />
              ))}
            </Feed>
          )}
        </Card>
      </div>

      {nova && podeCriar && (
        <ModalDeReserva
          grade={grade}
          inicial={{
            quadraId: params.get('quadra') ?? undefined,
            hora: params.get('hora') ?? undefined,
          }}
          onFechar={fecharNova}
        />
      )}

      {marcacaoAberta && (
        <ModalDaMarcacao id={marcacaoAberta} grade={grade} onFechar={fecharMarcacao} />
      )}

      {previsto && (
        <ModalDoPrevistoDeLocacao data={grade.data} onFechar={() => setPrevisto(false)} />
      )}

      {mover && (
        <ConfirmacaoDeSerie
          pedido={mover}
          grade={grade}
          onFechar={() => setMover(null)}
          onEscolher={(escopo) => {
            const pedido = mover;
            setMover(null);
            void moverPara(pedido, escopo);
          }}
        />
      )}
    </>
  );
}

function CabecalhoDaAgenda({
  titulo,
  subtitulo,
  acoes,
}: {
  titulo: string;
  subtitulo?: string;
  acoes?: React.ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <h1>{titulo}</h1>
        {subtitulo && <p>{subtitulo}</p>}
      </div>
      {acoes && <div className="head-actions">{acoes}</div>}
    </div>
  );
}

const TIPO_NO_PLURAL: Record<TipoMarcacao, [string, string]> = {
  LocacaoAvulsa: ['locação', 'locações'],
  Evento: ['evento', 'eventos'],
  Bloqueio: ['bloqueio', 'bloqueios'],
  TurmaFixa: ['aula de turma', 'aulas de turma'],
  Experimental: ['experimental', 'experimentais'],
};

/**
 * Os KPIs do topo de `#v-agenda`. O "Previsto de locação" só aparece
 * para quem a projeção deixou ver valor — o servidor simplesmente não
 * manda o número para o Professor, e a tela não inventa um zero.
 */
function IndicadoresDoDia({
  grade,
  ehHoje,
  onAbrirPrevisto,
}: {
  grade: Grade;
  ehHoje: boolean;
  onAbrirPrevisto: () => void;
}) {
  const resumo = grade.resumo;
  const nomeDaQuadra = new Map(grade.quadras.map((q) => [q.id, q.nome]));

  const ocupacao =
    resumo.minutosOcupados % 60 === 0 && resumo.minutosDisponiveis % 60 === 0
      ? `${resumo.minutosOcupados / 60} de ${resumo.minutosDisponiveis / 60} h`
      : `${horas(resumo.minutosOcupados)} de ${horas(resumo.minutosDisponiveis)}`;

  const porQuadra = resumo.porQuadra
    .map(
      (q) =>
        `${nomeDaQuadra.get(q.quadraId) ?? 'Quadra'}: ${q.minutosOcupados ? horas(q.minutosOcupados) : '0 h'}`,
    )
    .join(' · ');

  const porTipo = (Object.entries(resumo.porTipo) as [TipoMarcacao, number][])
    .map(([tipo, n]) => `${n} ${TIPO_NO_PLURAL[tipo][n === 1 ? 0 : 1]}`)
    .join(' · ');

  const livres = grade.janelasLivres.reduce((soma, j) => soma + j.minutos, 0);
  const maior = [...grade.janelasLivres].sort((a, b) => b.minutos - a.minutos)[0];

  const indicadores = [
    <Kpi
      key="ocupacao"
      valor={ocupacao}
      rotulo="Ocupação do dia"
      tendencia="estavel"
      variacao={porQuadra}
    />,
  ];

  if (resumo.previstoLocacao !== undefined) {
    const pagas = resumo.locacoesPagas ?? 0;
    indicadores.push(
      <Kpi
        key="previsto"
        valor={dinheiro(resumo.previstoLocacao)}
        rotulo="Previsto de locação"
        tendencia={pagas > 0 ? 'alta' : 'estavel'}
        variacao={
          pagas > 0
            ? `${pagas} ${pagas === 1 ? 'reserva paga' : 'reservas pagas'} · ver em aberto`
            : 'nenhuma paga ainda · ver em aberto'
        }
        onClick={onAbrirPrevisto}
      />,
    );
  }

  indicadores.push(
    <Kpi
      key="marcacoes"
      valor={resumo.marcacoes}
      rotulo="Marcações no dia"
      tendencia="estavel"
      variacao={porTipo || 'nenhuma ainda'}
    />,
    <Kpi
      key="livres"
      valor={livres ? horas(livres) : '0 h'}
      rotulo={ehHoje ? 'Livre daqui para frente' : 'Livre para reserva'}
      tendencia="estavel"
      variacao={
        maior
          ? `maior: ${nomeDaQuadra.get(maior.quadraId) ?? 'Quadra'} · ${intervaloCurto(maior.inicio, maior.fim, grade.fuso)}`
          : 'sem janelas'
      }
    />,
  );

  return <div className={`grid ${indicadores.length === 4 ? 'g4' : 'g3'}`}>{indicadores}</div>;
}

/**
 * O drill-down do KPI "Previsto de locação" — `#m-kpi` aplicado a ele.
 *
 * O número sozinho dizia quanto o dia vale; não dizia o que falta
 * receber, de quem, nem em que horário. É a informação que a recepção
 * precisa para fechar o caixa — e daqui a baixa é um clique, sem passar
 * pela grade.
 */
function ModalDoPrevistoDeLocacao({ data, onFechar }: { data: string; onFechar: () => void }) {
  const consulta = useConsultaDeHorasACobrar({ de: data, ate: data });

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Previsto de locação"
      descricao={dataPorExtenso(data, true)}
      rodape={<Botao onClick={onFechar}>Fechar</Botao>}
    >
      <CorpoDeModal>
        {consulta.isPending ? (
          <EsqueletoDeCard linhas={4} />
        ) : consulta.isError ? (
          <Erro
            detalhe={mensagemDoErro(consulta.error)}
            onTentarDeNovo={() => void consulta.refetch()}
          />
        ) : (
          <>
            <ResumoDeHorasACobrar dados={consulta.data} />
            <p className="sub">
              Cada linha é uma hora de quadra do dia. Clique para dar baixa — o recebimento vira
              lançamento no financeiro, na data que você informar.
            </p>
            <ListaDeHorasACobrar
              dados={consulta.data}
              comRecebidas
              vazio="Nenhuma locação nem evento neste dia."
            />
          </>
        )}
      </CorpoDeModal>
    </Modal>
  );
}

/**
 * A pergunta que o protótipo promete ao arrastar um card de série:
 * "somente esta aula ou toda a série?".
 */
function ConfirmacaoDeSerie({
  pedido,
  grade,
  onFechar,
  onEscolher,
}: {
  pedido: PedidoDeMover;
  grade: Grade;
  onFechar: () => void;
  onEscolher: (escopo: EscopoDeAlteracao) => void;
}) {
  const nome = (id: string) => grade.quadras.find((q) => q.id === id)?.nome ?? 'quadra';
  const de = `${nome(pedido.marcacao.quadraId)} às ${horaCurta(horaNaArena(pedido.marcacao.inicio, grade.fuso))}`;
  const para = `${nome(pedido.quadraId)} às ${horaCurta(horaNoRelogio(pedido.hora))}`;
  const dia = dataPorExtenso(partesNoFuso(pedido.marcacao.inicio, grade.fuso).data);

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      titulo="Mover marcação da série"
      descricao={`${dia} · ${de} → ${para}`}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao variante="contorno" onClick={() => onEscolher('ocorrencia')}>
            Somente esta
          </Botao>
          <Botao onClick={() => onEscolher('serie')}>Toda a série</Botao>
        </>
      }
    >
      <CorpoDeModal>
        <p className="sub">
          A alteração vale <b>somente para esta data</b> ou para{' '}
          <b>toda a série, desta em diante</b>? As datas que já passaram não mudam, e as que foram
          remarcadas uma a uma mantêm o horário próprio.
        </p>
      </CorpoDeModal>
    </Modal>
  );
}
